/**
 * How a group's drops use its lists (user, 19/10/2026: "I don't understand
 * the difference between every list and alternate … give an animation").
 * One choice of three cards — One list · Every list · Alternate, each with
 * its pattern drawn — then, for the two all-lists ways, how many of the
 * best lists; today on one line, the verdict a quiet note at its end
 * (02/11/2026 — "really messy and not readable"); rank slots.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';

import { NumberField } from '@/components/ui';
import { cn } from '@/lib/utils';

import type { DraftGroupRef } from '../../../../drop-generator.types';
import { MINIMAL_FIELD } from '../../general/controls/field-style';
import type { ContainerEditor } from '../../state/useContainerEditor';
import { listModeOf, modePatch } from '../list-choice/list-mode';
import { ModeCards } from '../list-choice/ModeCards';
import type { GroupDay } from './group-days';
import { ALL_LISTS_CAP, allListsCount, rankWords, verdictWords } from './list-choice';
import { RankSlotsCard } from './RankSlotsCard';
import { TodayLine } from './TodayLine';

const LABEL = 'text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground';

interface Props {
  link: DraftGroupRef;
  groupName: string;
  /** The lists the group matches now (null = not known yet). */
  matchCount: number | null;
  /** The list verdict is on: a terrible list leaves the order. */
  verdictOn: boolean;
  editor: ContainerEditor;
  day: GroupDay | undefined;
  onPatch: (patch: Partial<DraftGroupRef>) => void;
}

export const ListChoicePanel: FC<Props> = ({ link, groupName, matchCount, verdictOn, editor, day, onPatch }) => {
  const mode = listModeOf(link);
  const lists = mode === 'one' ? Math.min(1, matchCount ?? 1) : (allListsCount(link.listsPerDay, matchCount) ?? link.listsPerDay);
  return (
    <section className="flex flex-col gap-2 border-t border-border/50 px-3 py-2" data-testid="list-choice">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h4 className={LABEL}>How its drops use the lists</h4>
        {mode === 'one' ? <MatchCount count={matchCount} /> : <LimitLine link={link} matchCount={matchCount} onPatch={onPatch} />}
      </div>
      <ModeCards mode={mode} onSelect={(m) => onPatch(modePatch(m, link.listsPerDay))} />
      {mode !== 'one' ? <RankedNote drops={day?.drops ?? []} /> : null}
      <TodayLine
        mode={mode}
        drops={day?.drops ?? []}
        lists={lists}
        names={day?.names ?? []}
        fixed={day?.fixed ?? 0}
        verdict={{ on: verdictOn, words: verdictWords(mode === 'one', verdictOn, link.orderMode === 'FORMULA') }}
      />
      {mode !== 'one' ? <RankSlotsCard link={link} groupName={groupName} editor={editor} listCount={matchCount} /> : null}
    </section>
  );
};

const MatchCount: FC<{ count: number | null }> = ({ count }) =>
  count === null ? null : (
    <span className={cn('text-xs tabular-nums', count === 0 ? 'font-medium text-warning' : 'text-muted-foreground')}>
      {count === 0 ? 'No list matches today' : `${count.toLocaleString('en-US')} ${count === 1 ? 'list matches' : 'lists match'} today`}
    </span>
  );

/** "Use the best [10] of 132 lists, ranked by the formula." */
const LimitLine: FC<{ link: DraftGroupRef; matchCount: number | null; onPatch: (p: Partial<DraftGroupRef>) => void }> = ({ link, matchCount, onPatch }) => (
  <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground" title={`Lists ranked ${rankWords(link.orderMode)}.`}>
    Best
    <NumberField
      value={link.listsPerDay > 0 ? link.listsPerDay : ALL_LISTS_CAP.default}
      onChange={(v) => onPatch({ listsPerDay: Math.max(ALL_LISTS_CAP.min, Math.min(ALL_LISTS_CAP.max, Math.floor(v))) })}
      min={ALL_LISTS_CAP.min}
      max={ALL_LISTS_CAP.max}
      aria-label="The most lists the drops use"
      className={cn(MINIMAL_FIELD, 'h-6 w-12 text-center text-xs')}
    />
    {matchCount !== null ? `of ${matchCount.toLocaleString('en-US')} lists` : 'lists'}
    {matchCount === 0 ? <span className="text-warning">No list matches today</span> : null}
  </div>
);

/** Drops tied to a rank ignore Every list / Alternate — say so (user's RAW: every drop ranked, the choice changed nothing). */
const RankedNote: FC<{ drops: GroupDay['drops'] }> = ({ drops }) => {
  const ranked = drops.filter((d) => d.rank !== undefined).length;
  if (ranked === 0) return null;
  const words =
    ranked === drops.length
      ? `All ${ranked} drops are tied to a rank, so this choice doesn’t change today — the ranks decide. It applies to drops without a rank.`
      : `${ranked} of ${drops.length} drops are tied to a rank and keep it; this choice applies to the other ${drops.length - ranked}.`;
  return <p className="-mt-1 text-[11px] leading-snug text-primary">{words}</p>;
};
